import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  TextField,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import { API_KEYS_URL } from '../links';

function FAQ() {
  const [openSection, setOpenSection] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setShowSuggestions(false);
  };

  const faqContent = [
    {
      section: 'general',
      title: 'General Questions',
      questions: [
        { q: 'What is Sentinel?', a: 'Sentinel is a mobile application that allows users to report and monitor nearby dangers...' },
        { q: 'What is Sleuth Net?', a: 'Sleuth Net is a web-based application that enables users to report and monitor hazards worldwide...' },
        { q: 'What is Sleuth Net Dashboard?', a: 'Sleuth Net Dashboard is an analytics platform that compiles data from Sentinel...' },
        { q: 'What types of reports can be submitted through Sentinel and Sleuth Net?', a: 'Users can submit reports on: Unexploded Ordnance (UXOs), Military Activity...' },
        { q: 'What third-party data sources are integrated into the platform?', a: 'We integrate: NASA EONet Events, Missing Persons Reports...' },
        { q: 'Why should I use these apps?', a: 'Our tools offer real-time notifications, community-driven insights...' },
        { q: 'How do we ensure data accuracy?', a: 'We validate reports through community feedback, external sources...' },
        { q: 'How do I get started?', a: 'Download Sentinel from your app store, visit www.sleuthnet.com...' },
      ],
      content: (
        <>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            What is Sentinel?
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Sentinel is a mobile application that allows users to report and monitor nearby dangers, such as war crimes, natural disasters, unexploded ordnance (UXOs), and more. It provides real-time alerts and fosters community-driven reporting for enhanced situational awareness.
          </Typography>

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            What is Sleuth Net?
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Sleuth Net is a web-based application that enables users to report and monitor hazards worldwide, including climate impacts, infrastructure issues, and security threats. It integrates data from Sentinel and third-party sources for comprehensive updates.
          </Typography>

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            What is Sleuth Net Dashboard?
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Sleuth Net Dashboard is an analytics platform that compiles data from Sentinel, Sleuth Net, and external partners (e.g., NASA EONet, NamUs). It allows users to analyze nearby dangers and make informed decisions.
          </Typography>

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            What types of reports can be submitted through Sentinel and Sleuth Net?
          </Typography>
          <Typography variant="body1">
            Users can submit reports on:
          </Typography>
          <ul>
            <li>Unexploded Ordnance (UXOs)</li>
            <li>Military Activity</li>
            <li>Climate Impacts</li>
            <li>Structural Hazards</li>
            <li>Terrorist Activity</li>
            <li>Criminal Activity</li>
            <li>War Crimes</li>
            <li>Wildlife Hazards</li>
            <li>Resource Scarcity</li>
            <li>Public Health Hazards</li>
            <li>Cyber Threats</li>
            <li>Transportation Hazards</li>
            <li>Environmental Hazards</li>
            <li>Human Trafficking</li>
          </ul>
          <br />

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            What third-party data sources are integrated into the platform?
          </Typography>
          <Typography variant="body1">
            We integrate:
          </Typography>
          <ul>
            <li>
              <strong>NASA EONet Events</strong>: Tracks environmental events like wildfires and storms.
            </li>
            <li>
              <strong>Missing Persons Reports</strong>: BOLO reports from the National Missing and Unidentified Persons System (NamUs).
            </li>
          </ul>
          <br />

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Why should I use these apps?
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Our tools offer real-time notifications, community-driven insights, and validated data to keep you safe and informed about local and global hazards.
          </Typography>

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            How do we ensure data accuracy?
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            We validate reports through community feedback, external sources (e.g., NASA), and normalization tools (e.g., ethnicity mappings). Errors are logged for review.
          </Typography>

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            How do I get started?
          </Typography>
          <Typography variant="body1">
            Download Sentinel from your GooglePlay store, visit{' '}
            <a href="http://www.sleuthnet.com" target="_blank" rel="noopener noreferrer">
              www.sleuthnet.com
            </a>{' '}
            for Sleuth Net, or email{' '}
            <a href="mailto:support@arclighttech.com">support@arclighttech.com</a> for Sleuth Net Dashboard access.
          </Typography>
        </>
      ),
    },
    {
      section: 'privacy',
      title: 'Data Privacy & Security',
      questions: [
        { q: 'What personal data do you collect?', a: 'We collect: Contact Details, Payment Information...' },
        { q: 'What additional data is collected by the Sentinel app?', a: 'With your consent, Sentinel may collect: Geolocation Data...' },
        { q: 'How is my personal data processed?', a: 'We process data based on: User Consent...' },
        { q: 'Is my data shared or sold to third parties?', a: 'We may share data with trusted partners...' },
        { q: 'How is my data secured?', a: 'We use technical and organizational measures...' },
        { q: 'What rights do I have over my data?', a: 'You can: Access, correct, or delete your data...' },
      ],
      content: (
        <>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            What personal data do you collect?
          </Typography>
          <Typography variant="body1">
            We collect:
          </Typography>
          <ul>
            <li>
              <strong>Contact Details</strong>: Name, email, phone number, address, education, profession.
            </li>
            <li>
              <strong>Payment Information</strong>: Bank and billing details (if applicable).
            </li>
            <li>
              <strong>User-Submitted Data</strong>: Reports, forms, and content you provide.
            </li>
          </ul>
          <br />

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            What additional data is collected by the Sentinel app?
          </Typography>
          <Typography variant="body1">
            With your consent, Sentinel may collect:
          </Typography>
          <ul>
            <li>
              <strong>Geolocation Data</strong>: For location-based alerts (including background tracking).
            </li>
            <li>
              <strong>Camera Access</strong>: For photo/video reporting.
            </li>
            <li>
              <strong>External Storage Access</strong>: To manage files.
            </li>
            <li>
              <strong>Wi-Fi Information</strong>: For network services.
            </li>
            <li>
              <strong>Internet Access</strong>: For online features.
            </li>
            <li>
              <strong>Push Notifications</strong>: For timely updates.
            </li>
          </ul>
          <br />

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            How is my personal data processed?
          </Typography>
          <Typography variant="body1">
            We process data based on:
          </Typography>
          <ul>
            <li>
              <strong>User Consent</strong>
            </li>
            <li>
              <strong>Contractual Necessity</strong> (e.g., subscriptions)
            </li>
            <li>
              <strong>Legal Compliance</strong>
            </li>
            <li>
              <strong>Protection of Vital Interests</strong>
            </li>
          </ul>
          <br />

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Is my data shared or sold to third parties?
          </Typography>
          <Typography variant="body1">
            We may share data with trusted partners who help operate our services (e.g., app functionality). We don’t sell data for marketing. Data may also be disclosed:
          </Typography>
          <ul>
            <li>To comply with legal obligations or court orders.</li>
            <li>To enforce policies or protect rights.</li>
            <li>In response to lawful government requests (e.g., national security).</li>
          </ul>
          <br />

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            How is my data secured?
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            We use technical and organizational measures to protect your data from unauthorized access, loss, or alteration. However, no electronic system is 100% secure.
          </Typography>

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            What rights do I have over my data?
          </Typography>
          <Typography variant="body1">
            You can:
          </Typography>
          <ul>
            <li>Access, correct, or delete your data.</li>
            <li>Object to or restrict processing.</li>
            <li>Request data portability.</li>
          </ul>
          <Typography variant="body1">
            Email <a href="mailto:support@arclighttech.com">support@arclighttech.com</a> to exercise these rights—we may verify your identity.
          </Typography>
        </>
      ),
    },
    {
      section: 'retention',
      title: 'Data Retention & Deletion',
      questions: [
        { q: 'How long is my data retained?', a: 'We keep data as long as needed to: Provide services...' },
        { q: 'How can I delete my account and data?', a: 'Delete your account via: Sentinel App...' },
      ],
      content: (
        <>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            How long is my data retained?
          </Typography>
          <Typography variant="body1">
            We keep data as long as needed to:
          </Typography>
          <ul>
            <li>Provide services.</li>
            <li>Comply with legal obligations.</li>
            <li>Resolve disputes.</li>
          </ul>
          <Typography variant="body1" sx={{ mb: 2 }}>
            After account deletion, data is held for <strong>60 days</strong> for recovery, then securely deleted unless legally required otherwise.
          </Typography>

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            How can I delete my account and data?
          </Typography>
          <Typography variant="body1">
            Delete your account via:
          </Typography>
          <ol>
            <li>
              <strong>Sentinel App</strong>: Go to settings and select "Delete Account."
            </li>
            <li>
              <strong>Website</strong>: Visit{' '}
              <a href="http://www.sleuthnet.com/delete-account" target="_blank" rel="noopener noreferrer">
                www.sleuthnet.com/delete-account
              </a>.
            </li>
            <li>
              <strong>Email</strong>: Contact <a href="mailto:support@arclighttech.com">support@arclighttech.com</a>.
            </li>
          </ol>
          <Typography variant="body1">
            Deletion occurs within <strong>60 days</strong>, except for legally retained data.
          </Typography>
        </>
      ),
    },
    {
      section: 'legal',
      title: 'Legal & Compliance',
      questions: [
        { q: 'Do you comply with child protection laws?', a: 'Yes, we comply with COPPA (US) and GDPR (EU) by...' },
        { q: 'What happens if the Privacy Policy changes?', a: 'We’ll notify you of significant updates via a popup...' },
      ],
      content: (
        <>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Do you comply with child protection laws?
          </Typography>
          <Typography variant="body1">
            Yes, we comply with <strong>COPPA (US)</strong> and <strong>GDPR (EU)</strong> by:
          </Typography>
          <ul>
            <li>Offering age-appropriate content.</li>
            <li>Displaying child-friendly ads.</li>
            <li>Using neutral age screens.</li>
          </ul>
          <br />

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            What happens if the Privacy Policy changes?
          </Typography>
          <Typography variant="body1">
            We’ll notify you of significant updates via a popup. Continued use after notification implies acceptance of the new terms. If you disagree, you’ll lose access to our services.
          </Typography>
        </>
      ),
    },
    {
      section: 'roles',
      title: 'User Roles & Membership',
      questions: [
        { q: 'Basic User', a: 'Access: Sentinel, SleuthNet, SNDashboard...' },
        { q: 'SuperUser', a: 'Access: Sentinel, SleuthNet, SNDashboard...' },
        { q: 'Expert User', a: 'Access: Sentinel...' },
      ],
      content: (
        <>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Basic User
          </Typography>
          <Typography variant="body1">
            <strong>Access</strong>: Sentinel, SleuthNet, SNDashboard
          </Typography>
          <Typography variant="body1">
            <strong>Permissions</strong>:
          </Typography>
          <ul>
            <li>View and submit reports</li>
            <li>Check general info on the dashboard</li>
          </ul>
          <Typography variant="body1">
            <strong>Restrictions</strong>:
          </Typography>
          <ul>
            <li>Cannot edit or update reports</li>
            <li>Cannot update safety or missing statuses</li>
            <li>Cannot add materials or comments in SNDashboard</li>
            <li>Cannot see military activity reports until 48 hours after submission</li>
            <li>Cannot generate CSV or PDF files in SNDashboard</li>
          </ul>
          <br />

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            SuperUser
          </Typography>
          <Typography variant="body1">
            <strong>Access</strong>: Sentinel, SleuthNet, SNDashboard
          </Typography>
          <Typography variant="body1">
            <strong>Permissions</strong>:
          </Typography>
          <ul>
            <li>View reports</li>
            <li>Check general info on the dashboard</li>
            <li>Submit, edit, and update reports</li>
            <li>Update safety and missing statuses</li>
            <li>Add materials or comments in SNDashboard</li>
            <li>View submitted military activity reports immediately after they have been submitted</li>
            <li>Export CSV and PDF files in SNDashboard</li>
          </ul>
          <br />

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Expert User
          </Typography>
          <Typography variant="body1">
            <strong>Access</strong>: Sentinel
          </Typography>
          <Typography variant="body1">
            <strong>Permissions</strong>: (Details to be defined)
          </Typography>
        </>
      ),
    },
    {
      section: 'technical',
      title: 'Technical & Support',
      questions: [
       
        { q: 'Can I contribute photos or videos?', a: 'Yes, by submitting content, you grant us a worldwide...' },
        { q: 'How can I contact support?', a: 'Email us at support@arclighttech.com for help...' },
      ],
      content: (
        <>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Can I contribute photos or videos?
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Yes, by submitting content, you grant us a worldwide, royalty-free license to use it for alerts or promotion. You must own the rights to what you share.
          </Typography>

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            How can I contact support?
          </Typography>
          <Typography variant="body1">
            Email us at <strong><a href="mailto:support@arclighttech.com">support@arclighttech.com</a></strong> for help with accounts, features, or inquiries.
          </Typography>
        </>
      ),
    },
    {
      section: 'api',
      title: 'API Questions',
      questions: [
        { q: 'What does the Arc Light Technologies API do?', a: 'The API provides access to real-time open-source intelligence data...' },
        { q: 'How do I get an API key?', a: 'You can request an API key by following the instructions on our API Key Issuance page...' },
        { q: 'What are the rate limits for the API?', a: 'Rate limits apply to your account and depend on your plan (Professional/Business/Enterprise)...' },
        { q: 'Where can I find more details about the API?', a: 'Check out our API Documentation page for detailed information...' },
      ],
      content: (
        <>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            What does the Arc Light Technologies API do?
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            The API provides access to real-time open-source intelligence data, including granular reports and aggregated analytics, to help you integrate our data into your applications.
          </Typography>

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            How do I get an API key?
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You can request an API key by following the instructions on our{' '}
            <a href={API_KEYS_URL} target="_blank" rel="noopener noreferrer">SleuthNet profile page</a> (API tab).
          </Typography>

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            What are the rate limits for the API?
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Rate limits apply to your account, not per key or IP (a new key keeps your usage), and follow your plan: Professional (100/hour, 2,000/month), Business (1,000/hour, 50,000/month), or Enterprise (10,000/hour, custom monthly quota). Exceeding either limit returns a 429 Too Many Requests response with a Retry-After header.
          </Typography>

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Where can I find more details about the API?
          </Typography>
          <Typography variant="body1">
            Check out our <a href="/docs">API Documentation page</a> for detailed information on endpoints, parameters, and usage.
          </Typography>
        </>
      ),
    },
  ];

  const filteredFaqContent = faqContent.filter((item) => {
    const contentString = item.title.toLowerCase() + item.content.props.children.toString().toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    return searchTerm === '' || item.section === openSection || contentString.includes(searchLower);
  });

  const suggestions = faqContent
    .flatMap((item) =>
      item.questions.map((q) => ({
        question: q.q,
        section: item.section,
      }))
    )
    .filter((suggestion) => searchTerm && suggestion.question.toLowerCase().includes(searchTerm.toLowerCase()))
    .slice(0, 5);

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.question);
    setOpenSection(suggestion.section);
    setShowSuggestions(false);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" align="center" sx={{ fontWeight: 'bold', mb: 4 }}>
        Frequently Asked Questions (FAQ)
      </Typography>

      {/* Search Field */}
      <Box sx={{ mb: 4, position: 'relative' }}>
        <TextField
          fullWidth
          label="Search FAQs..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setShowSuggestions(e.target.value.length > 0);
          }}
          onFocus={() => setShowSuggestions(searchTerm.length > 0)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          InputProps={{
            endAdornment: searchTerm && (
              <IconButton onClick={clearSearch} aria-label="Clear search">
                <ClearIcon />
              </IconButton>
            ),
          }}
        />
        {showSuggestions && suggestions.length > 0 && (
          <List
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              bgcolor: 'background.paper',
              boxShadow: 3,
              borderRadius: 1,
              zIndex: 1,
              maxHeight: 200,
              overflowY: 'auto',
            }}
          >
            {suggestions.map((suggestion, index) => (
              <ListItem
                key={index}
                button
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <ListItemText primary={suggestion.question} />
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* FAQ Sections */}
      <Box>
        {filteredFaqContent.length > 0 ? (
          filteredFaqContent.map((item) => (
            <Accordion
              key={item.section}
              expanded={openSection === item.section}
              onChange={() => toggleSection(item.section)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {item.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>{item.content}</AccordionDetails>
            </Accordion>
          ))
        ) : (
          <Typography align="center" sx={{ mt: 2 }}>
            No results found for "{searchTerm}".
          </Typography>
        )}
      </Box>

      <Typography align="center" sx={{ mt: 4 }}>
        <strong>Still have questions?</strong> Reach out at{' '}
        <strong>
          <a href="mailto:support@arclighttech.com">support@arclighttech.com</a>
        </strong>{' '}
        or visit{' '}
        <a href="http://www.sleuthnet.com" target="_blank" rel="noopener noreferrer">
          www.sleuthnet.com
        </a>. Stay vigilant, stay informed!
      </Typography>
    </Container>
  );
}

export default FAQ;